/**
 * Track Card Component
 * Compact card with YouTube player and album art
 */

import { useState } from 'react';
import { EnrichedTrack } from '../types/track.types';

interface TrackCardProps {
  track: EnrichedTrack;
  selected?: boolean;
  onToggleSelect?: (trackId: string) => void;
  onAddAsNode?: (artist: string) => void;
  connectionType?: string;
}

const CONNECTION_TYPE_STYLES: Record<string, string> = {
  'founding technique': 'bg-amber-100 text-amber-800',
  'peak era': 'bg-blue-100 text-blue-800',
  'modern inheritor': 'bg-green-100 text-green-800',
  'unexpected angle': 'bg-purple-100 text-purple-800',
  'scene origin': 'bg-orange-100 text-orange-800',
  'adjacent influence': 'bg-gray-100 text-gray-700',
};

// Extract YouTube video ID from URL
function getYouTubeId(url: string): string | null {
  const match = url.match(/(?:youtu\.be\/|youtube\.com(?:\/embed\/|\/v\/|\/watch\?v=|\/watch\?.+&v=))([^&?]+)/);
  return match ? match[1] : null;
}

// Generate placeholder image URL (using a placeholder service)
function getPlaceholderImage(artist: string): string {
  const encodedArtist = encodeURIComponent(artist);
  // Using UI Avatars as fallback
  return `https://ui-avatars.com/api/?name=${encodedArtist}&size=300&background=random&color=fff&bold=true`;
}

export default function TrackCard({ track, selected = false, onToggleSelect, onAddAsNode, connectionType }: TrackCardProps) {
  const [showPlayer, setShowPlayer] = useState(false);
  const youtubeId = track.youtube_url ? getYouTubeId(track.youtube_url) : null;

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all overflow-hidden">
      {/* Image/Video Area */}
      <div className="relative aspect-video bg-gradient-to-br from-gray-100 to-gray-200">
        {showPlayer && youtubeId ? (
          <iframe
            className="w-full h-full"
            src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <div
            className="w-full h-full bg-cover bg-center cursor-pointer group relative"
            style={{ backgroundImage: `url(${getPlaceholderImage(track.artist)})` }}
            onClick={() => youtubeId && setShowPlayer(true)}
          >
            {/* Dark overlay */}
            <div className="absolute inset-0 bg-black bg-opacity-30 group-hover:bg-opacity-40 transition-opacity" />

            {/* Play button */}
            {youtubeId && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                  <svg className="w-8 h-8 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                </div>
              </div>
            )}

            {/* Selection checkbox */}
            {onToggleSelect && (
              <div className="absolute top-2 left-2 z-10" onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={() => onToggleSelect(track.id)}
                  className="w-5 h-5 text-blue-600 bg-white rounded focus:ring-2 focus:ring-blue-500 border-2 border-gray-300 cursor-pointer"
                />
              </div>
            )}

            {/* Verified badge */}
            {track.verified && (
              <div className="absolute top-2 right-2">
                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-green-600 rounded-full shadow-lg">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                  </svg>
                  Verified
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Artist & Title */}
        <div className="mb-2">
          <h3 className="font-semibold text-base text-gray-900 truncate">
            {track.artist}
          </h3>
          <p className="text-sm text-gray-700 truncate">
            {track.title}
          </p>
        </div>

        {/* Album & Year */}
        {(track.album || track.year) && (
          <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
            {track.album && <span className="truncate">{track.album}</span>}
            {track.album && track.year && <span>•</span>}
            {track.year && <span>{track.year}</span>}
          </div>
        )}

        {/* Connection type badge */}
        {connectionType && (
          <div className="mb-2">
            <span
              className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full ${
                CONNECTION_TYPE_STYLES[connectionType] || 'bg-gray-100 text-gray-700'
              }`}
            >
              {connectionType}
            </span>
          </div>
        )}

        {/* Reasoning */}
        {track.reasoning && (
          <p className="text-xs text-gray-600 mb-3 leading-relaxed">
            {track.reasoning}
          </p>
        )}

        {/* Platform Links */}
        <div className="flex items-center gap-2 flex-wrap">
          {track.bandcamp_url && (
            <a
              href={track.bandcamp_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                <path d="M0 18.75l7.437-13.5h16.563l-7.438 13.5z"/>
              </svg>
              Bandcamp
            </a>
          )}
          {track.youtube_url && !showPlayer && (
            <button
              onClick={() => setShowPlayer(true)}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-700 bg-red-50 rounded hover:bg-red-100 transition-colors"
            >
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
              Watch
            </button>
          )}
          {onAddAsNode && (
            <button
              onClick={() => onAddAsNode(track.artist)}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-purple-700 bg-purple-50 rounded hover:bg-purple-100 transition-colors"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Add Artist
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
